/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstObjectModel,
	isSchemaConstBooleanModel,
	isSchemaConstObjectModel,
	isSchemaValueModel,
	SCHEMA_VALUE_TYPE,
	TGenericModelNode,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import {
	IInspectorNode_Default,
	INSPECTOR_NODE_TYPE,
	isInspectorNodeDefault,
	TInspectorNode
} from "../InspectorNodeTypes";
import { mapModelToNodes } from "../index";
import { createDefaultNode } from "../createDefaultNode";
import { IInspectorControl_ButtonGroup, INSPECTOR_CONTROL_TYPE, TInspectorControl } from "../controls";
import { getParentComponentName, isHidden } from "../utils";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";

export interface IConstObject_EditorMetaData {
	showExpanded?: boolean;
}

export function constObjectMapper(model: ISchemaConstObjectModel<TSchemaConstObjectProps>): TInspectorNode {
	if (!isSchemaConstObjectModel(model)) {
		return;
	}

	const propKeys = Object.keys(model.props);
	const deps: IInspectorNode_Default["deps"] = [ model ];

	if (model.schema.opts.editorOptions?.controlType === "buttons") {
		const propsOnlyBool = propKeys.every((propKey) => {
			const propModel = model.props[propKey];
			const targetModel =
				isSchemaValueModel(propModel) && propModel.type === SCHEMA_VALUE_TYPE.CONST
					? propModel.constant
					: propModel;
			return isSchemaConstBooleanModel(targetModel);
		});

		if (propsOnlyBool) {
			const control: IInspectorControl_ButtonGroup = {
				key: String(model.nodeId),
				type: INSPECTOR_CONTROL_TYPE.BUTTON_GROUP,
				model
			};

			return createDefaultNode({
				key: String(model.nodeId),
				label: model.schema.opts.label,
				tooltip: model.schema.opts.description,
				controls: [ control ],
				deps: [ model ]
			});
		}
	}

	const singleChildModel = propKeys.length === 1 ? model.props[propKeys[0]] : null;

	let children: IInspectorNode_Default["children"];

	const controls: TInspectorControl[] = [];
	const collapsedControls: TInspectorControl[] = [];

	// If has only one prop and it's object, use that object's children
	if (singleChildModel && isSchemaConstObjectModel(singleChildModel)) {
		const childInspectorNode = mapModelToNodes(singleChildModel);
		if (isInspectorNodeDefault(childInspectorNode)) {
			children = childInspectorNode.children;
		}

		return childInspectorNode;
	}

	if (!children) {
		let lastId = 0;

		const groups: {
			[K: string]: {
				id: string;
				label: string;
				description?: string;
				order: number;
				defaultExpanded: boolean;
				items: Array<{
					model: TGenericModelNode;
					order: number;
					key: string;
				}>;
			};
		} = {};

		for (let i = 0; i < propKeys.length; i++) {
			const propKey = propKeys[i];
			const propModel = model.props[propKey];
			const propOpts = model.schema.opts.props[propKey];

			if (isHidden(propModel)) {
				continue;
			}

			const group = propOpts.group;

			if (group) {
				if (!groups[group.id]) {
					groups[group.id] = {
						id: group.id,
						label: group.label,
						description: group.description,
						order: group.order !== undefined ? group.order : 0,
						defaultExpanded: group.defaultExpanded,
						items: []
					};
				}

				groups[group.id].items.push({
					model: propModel,
					order: model.schema.opts.props[propKey].order,
					key: propKey
				});
			} else {
				groups["_" + lastId++] = {
					id: null,
					label: null,
					description: null,
					order: model.schema.opts.props[propKey].order,
					defaultExpanded: true,
					items: [
						{
							model: propModel,
							order: 0,
							key: propKey
						}
					]
				};
			}
		}

		const groupList = Object.keys(groups)
			.map((groupId) => {
				return {
					...groups[groupId],
					items: groups[groupId].items.sort((a, b) =>
						a.order > b.order ? 1 : a.order < b.order ? -1 : 0
					)
				};
			})
			.sort((a, b) => (a.order > b.order ? 1 : a.order < b.order ? -1 : 0));

		children = [];

		for (let i = 0; i < groupList.length; i++) {
			const group = groupList[i];

			if (group.id === "__primary__" && group.items.length === 1) {
				const itemNode = mapModelToNodes(group.items[0]?.model);
				collapsedControls.push(...itemNode.controls);
				deps.push(...itemNode.deps);
			}

			children.push(() => {
				const groupDeps = [];
				const groupChildren = [];
				const groupControls: TInspectorControl[] = [];
				const groupChildNodes: TInspectorNode[] = [];

				for (let i = 0; i < group.items.length; i++) {
					const item = group.items[i];
					const itemNode = mapModelToNodes(item.model);

					if (!itemNode) {
						continue;
					}

					if (group.id === null) {
						if (
							itemNode.controls.length > 0 ||
							itemNode.deps.length > 0 ||
							(isInspectorNodeDefault(itemNode) && itemNode.children.length > 0)
						) {
							itemNode.label = itemNode.label || item.model?.schema?.opts?.label || item.key;
						}

						if (item.model?.schema?.opts?.editorOptions?.layoutType !== "passthrough") {
							return itemNode;
						}
					}

					groupDeps.push(item.model);

					if (itemNode) {
						groupDeps.push(...itemNode.deps);
					}

					const passValueControl =
						group.items.length === 1 &&
						isInspectorNodeDefault(itemNode) &&
						!itemNode.label &&
						itemNode.controls.length === 1;

					if (passValueControl) {
						if (
							itemNode.controls.length === 1 &&
							itemNode.controls[0]?.type === INSPECTOR_CONTROL_TYPE.VALUE_TYPE
						) {
							groupControls.push(...itemNode.controls.splice(0, 1));
						}
					}

					groupChildren.push(() => {
						const childItemNode = mapModelToNodes(item.model);

						if (childItemNode) {
							if (isInspectorNodeDefault(childItemNode)) {
								if (!group.label && !childItemNode.label) {
									if (childItemNode.controls.length && !childItemNode.children.length) {
										childItemNode.label = item.key;
									}
								}
							}
						}

						if (passValueControl) {
							if (
								childItemNode.controls[childItemNode.controls.length - 1]?.type ===
								INSPECTOR_CONTROL_TYPE.VALUE_TYPE
							) {
								childItemNode.controls.splice(-1);
							}
						}

						groupChildNodes.push(childItemNode);

						return childItemNode;
					});
				}

				return {
					type: INSPECTOR_NODE_TYPE.DEFAULT,
					key: group.id,
					label: group.label,
					group: true,
					defaultCollapsed: group.defaultExpanded === false,
					tooltip: "",
					deps: groupDeps,
					controls: groupControls,
					children: groupChildren
				};
			});
		}
	}

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: "",
		tooltip: "",
		required: model.schema.opts.constraints?.required || false,
		default: model.schema.opts.default || null,
		options: model.schema.opts["options"],
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: deps,
		controls: controls,
		defaultCollapsed: model.schema.opts?.editorOptions?.defaultExpanded === false,
		collapsedControls: collapsedControls,
		children: children
	};
}
